"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import Button from "@/app/components/atoms/Button";
import { useModal } from "@/app/components/context/ModalContext";
import MemberQRCode from "@/app/components/molecules/MemberQRCode";
import StatusBadge from "@/app/components/atoms/StatusBadge";
import TextLink from "@/app/components/atoms/TextLink";
import Icon from "@/app/components/atoms/Icon";
import { event } from "@/lib/analytics/gtag";
import type {
  MembershipMember,
  MembershipStatus,
} from "@/app/components/types";

interface MemberDetailModalProps {
  member: MembershipMember;
  membershipType: string;
  membershipStatus: MembershipStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * @component MemberDetailModal
 * @description Modal showing full member details and a large QR code for park entry.
 * Displays membership type, status badge, large QR code, and member info (since, expires, ID).
 * @param member - Member data to display.
 * @param membershipType - Label for the membership tier.
 * @param membershipStatus - Current status used for badge styling.
 * @param open - Whether the modal is visible.
 * @param onOpenChange - Called with false when the modal should close.
 * @returns A full-screen mobile / centred desktop modal dialog.
 */
export default function MemberDetailModal({
  member,
  membershipType,
  membershipStatus,
  open,
  onOpenChange,
}: MemberDetailModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const isExpired = membershipStatus === "EXPIRED";
  const { openModal, closeModal } = useModal();

  // Register/deregister with the shared backdrop
  useEffect(() => {
    if (open) {
      openModal(() => onOpenChange(false));
    } else {
      closeModal();
    }
    return () => closeModal();
  }, [open, openModal, closeModal, onOpenChange]);

  // Trap focus on close button and close on Escape
  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-51 flex items-center justify-center pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-detail-title"
    >
      {/* Panel */}
      <div className="pointer-events-auto relative bg-white w-screen h-screen flex flex-col md:w-auto md:h-auto md:max-w-2xl md:min-w-[36rem] md:border md:shadow-lg">
        {/* Header */}
        <div className="p-6 pb-0 flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-body-base text-near-black inline-block">
                {membershipType}
              </p>
              <StatusBadge status={membershipStatus} />
            </div>
            <h2
              id="member-detail-title"
              className="text-heading-alt text-charcoal capitalize"
            >
              {member.name}
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={() => {
              event("member_card_closed", {
                category: "membership",
                label: member.name,
                membership_type: membershipType,
                membership_status: membershipStatus,
                is_primary: member.isPrimary ? "true" : "false",
              });
              onOpenChange(false);
            }}
            aria-label="Close"
            className="shrink-0 p-1 text-muted-text hover:text-charcoal min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>

        {/* QR Code — centred */}
        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className={isExpired ? "opacity-50" : ""}>
            <MemberQRCode memberId={member.id} size={200} />
          </div>
        </div>

        {/* Member Details */}
        <div className="px-6 space-y-1">
          {member.memberSince && (
            <p
              className={`text-body-base text-muted-text ${isExpired ? "opacity-50" : ""}`}
            >
              Member Since {member.memberSince}
            </p>
          )}
          {member.expires &&
            (isExpired ? (
              <p className="text-body-base text-white bg-near-black px-2 py-0.5 inline-block">
                Expired {member.expires}
              </p>
            ) : (
              <p className="text-body-base">
                <span className="text-muted-text">Expires </span>
                <span className="text-near-black">{member.expires}</span>
              </p>
            ))}
          <p
            className={`text-body-base text-muted-text ${isExpired ? "opacity-50" : ""}`}
          >
            ID: {member.id}
          </p>
          {isExpired && (
            <TextLink
              as="a"
              href={`${process.env.NEXT_PUBLIC_ALTRU_BASE_URL}/Membership`}
              className="mt-3 inline-flex"
              onClick={(e) => {
                e.stopPropagation();
                event("membership_renew_clicked", {
                  category: "membership",
                  label: member.name ?? undefined,
                  membership_type: membershipType,
                  membership_status: membershipStatus,
                  is_primary: member.isPrimary ? "true" : "false",
                  source: "modal",
                });
              }}
            >
              Renew Membership
              <Icon name="direct-arrow" className="w-4 h-4 brightness-0" />
            </TextLink>
          )}
        </div>

        {/* Done Button */}
        <div className="p-6">
          <Button
            variant="primary"
            size="full"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
